import React, { useEffect, useMemo, useState } from 'react';
import {
  Sliders,
  RotateCcw,
  Save,
  ChevronDown,
  ChevronUp,
  Info,
  AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

/**
 * Schema entry shape mirrors the JSON payload returned by
 * `GET /api/admin/settings/cloaking`. Keep the field names in sync
 * with `server/config/cloakingConfig.js` — the backend is the source
 * of truth for `default`, `min`, `max`, etc., so the UI never
 * drifts when we add or rename a knob.
 */
interface SchemaEntry {
  key: string;
  label: string;
  description: string;
  category: 'detection' | 'toggles' | 'rate' | 'reputation' | 'challenge' | 'verdict';
  cast: 'int' | 'float' | 'bool';
  min?: number;
  max?: number;
  step?: number;
  envVar: string | null;
  default: number | boolean;
  defaultEffective: number | boolean;
  effective: number | boolean;
}

interface AdminPayload {
  schema: SchemaEntry[];
  effective: Record<string, number | boolean>;
}

const CATEGORY_ORDER: SchemaEntry['category'][] = [
  'detection',
  'toggles',
  'rate',
  'reputation',
  'challenge',
  'verdict',
];

/**
 * Renders the admin-tunable cloaking config as a single collapsible
 * card. Defaults to collapsed because the page is busy enough; admins
 * who want to tune go one click in.
 *
 * Mounted both:
 *   • Inside AdminBotCloaking (above the per-page table) so the
 *     thresholds and the data they affect live next to each other.
 *   • As `defaultOpen` only when query string `?settings=1` is passed
 *     (caller decides — we just respect the prop).
 */
export const CloakingThresholdsPanel: React.FC<{ defaultOpen?: boolean }> = ({
  defaultOpen = false,
}) => {
  const { t } = useTranslation('admin');
  const token = useAuthStore((s) => s.token);
  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [payload, setPayload] = useState<AdminPayload | null>(null);
  const [draft, setDraft] = useState<Record<string, number | boolean>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/settings/cloaking`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: AdminPayload = await res.json();
      setPayload(json);
      setDraft({ ...json.effective });
      setValidationErrors({});
    } catch (err) {
      console.error('[CloakingThresholdsPanel] load error:', err);
      toast.error(t('botCloaking.thresholdsPanel.loadToastError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !payload) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dirtyKeys = useMemo(() => {
    if (!payload) return [];
    return payload.schema
      .filter((entry) => draft[entry.key] !== payload.effective[entry.key])
      .map((entry) => entry.key);
  }, [draft, payload]);

  const grouped = useMemo(() => {
    if (!payload) return null;
    const out: Record<SchemaEntry['category'], SchemaEntry[]> = {
      detection: [],
      toggles: [],
      rate: [],
      reputation: [],
      challenge: [],
      verdict: [],
    };
    for (const e of payload.schema) {
      out[e.category].push(e);
    }
    return out;
  }, [payload]);

  const handleNumberChange = (entry: SchemaEntry, raw: string) => {
    const errors = { ...validationErrors };
    delete errors[entry.key];
    if (raw === '' || raw === '-') {
      setDraft({ ...draft, [entry.key]: raw as unknown as number });
      setValidationErrors(errors);
      return;
    }
    const num = entry.cast === 'int' ? parseInt(raw, 10) : parseFloat(raw);
    if (Number.isNaN(num)) {
      errors[entry.key] = t('botCloaking.thresholdsPanel.validation.mustBeNumber');
    } else if (entry.min !== undefined && num < entry.min) {
      errors[entry.key] = t('botCloaking.thresholdsPanel.validation.mustBeGte', {
        min: entry.min,
      });
    } else if (entry.max !== undefined && num > entry.max) {
      errors[entry.key] = t('botCloaking.thresholdsPanel.validation.mustBeLte', {
        max: entry.max,
      });
    }
    setDraft({ ...draft, [entry.key]: num });
    setValidationErrors(errors);
  };

  const handleBoolChange = (entry: SchemaEntry, value: boolean) => {
    setDraft({ ...draft, [entry.key]: value });
  };

  const handleSave = async () => {
    if (!token || !payload) return;
    if (Object.keys(validationErrors).length > 0) {
      toast.error(t('botCloaking.thresholdsPanel.fixValidation'));
      return;
    }
    if (dirtyKeys.length === 0) {
      toast(t('botCloaking.thresholdsPanel.noChanges'), { icon: 'ℹ️' });
      return;
    }
    setSaving(true);
    const updates: Record<string, number | boolean> = {};
    for (const k of dirtyKeys) updates[k] = draft[k];
    try {
      const res = await fetch(`${API_URL}/admin/settings/cloaking`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(
          json?.details ? `${json.error}: ${json.details.join('; ')}` : json?.error || 'Save failed',
        );
      }
      toast.success(
        dirtyKeys.length === 1
          ? t('botCloaking.thresholdsPanel.savedSingle', { count: dirtyKeys.length })
          : t('botCloaking.thresholdsPanel.savedPlural', { count: dirtyKeys.length }),
      );
      setPayload({ schema: json.schema, effective: json.effective });
      setDraft({ ...json.effective });
    } catch (err) {
      console.error('[CloakingThresholdsPanel] save error:', err);
      toast.error(err instanceof Error ? err.message : t('botCloaking.thresholdsPanel.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    if (!token) return;
    const confirmed = window.confirm(t('botCloaking.thresholdsPanel.resetConfirm'));
    if (!confirmed) return;
    setResetting(true);
    try {
      const res = await fetch(`${API_URL}/admin/settings/cloaking/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || t('botCloaking.thresholdsPanel.resetFailed'));
      toast.success(t('botCloaking.thresholdsPanel.resetSuccess'));
      setPayload({ schema: json.schema, effective: json.effective });
      setDraft({ ...json.effective });
    } catch (err) {
      console.error('[CloakingThresholdsPanel] reset error:', err);
      toast.error(
        err instanceof Error ? err.message : t('botCloaking.thresholdsPanel.resetFailed'),
      );
    } finally {
      setResetting(false);
    }
  };

  const handleResetField = (entry: SchemaEntry) => {
    setDraft({ ...draft, [entry.key]: entry.defaultEffective });
    const errors = { ...validationErrors };
    delete errors[entry.key];
    setValidationErrors(errors);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl mb-6 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-gray-50 transition"
      >
        <div className="flex items-center gap-3 text-left">
          <Sliders className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div>
            <div className="text-sm font-semibold text-gray-900">
              {t('botCloaking.thresholdsPanel.title')}
              {dirtyKeys.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
                  {t('botCloaking.thresholdsPanel.unsavedCount', { count: dirtyKeys.length })}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {t('botCloaking.thresholdsPanel.subtitle')}
            </div>
          </div>
        </div>
        {open ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {open && (
        <div className="border-t border-gray-200">
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">
              {t('botCloaking.thresholdsPanel.loading')}
            </div>
          ) : !payload || !grouped ? (
            <div className="p-8 text-center text-sm text-rose-600">
              {t('botCloaking.thresholdsPanel.loadError')}
            </div>
          ) : (
            <>
              <div className="p-5 space-y-6">
                {CATEGORY_ORDER.map((cat) => {
                  const entries = grouped[cat];
                  if (!entries.length) return null;
                  return (
                    <section key={cat}>
                      <div className="mb-3">
                        <h3 className="text-sm font-semibold text-gray-900">
                          {t(`botCloaking.thresholdsPanel.categories.${cat}.label`)}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5 flex items-start gap-1.5">
                          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <span>{t(`botCloaking.thresholdsPanel.categories.${cat}.help`)}</span>
                        </p>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {entries.map((entry) => (
                          <ThresholdField
                            key={entry.key}
                            entry={entry}
                            displayLabel={t(`botCloaking.schema.${entry.key}.label`, {
                              defaultValue: entry.label,
                            })}
                            displayDescription={t(`botCloaking.schema.${entry.key}.description`, {
                              defaultValue: entry.description,
                            })}
                            value={draft[entry.key]}
                            isDirty={draft[entry.key] !== payload.effective[entry.key]}
                            error={validationErrors[entry.key]}
                            onNumberChange={handleNumberChange}
                            onBoolChange={handleBoolChange}
                            onResetField={handleResetField}
                          />
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>

              {/* Env-only secrets advisory — these MUST stay on Railway
                  because they're credentials, not behaviour knobs. We
                  list them here so admins don't go hunting in the wrong
                  place. */}
              <div className="px-5 pb-5">
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-xs text-blue-900">
                  <div className="font-semibold mb-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" />
                    {t('botCloaking.thresholdsPanel.railwayTitle')}
                  </div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 ml-5 list-disc">
                    <li>
                      <code className="font-mono">TURNSTILE_SITEKEY</code> &amp;{' '}
                      <code className="font-mono">TURNSTILE_SECRET_KEY</code>
                    </li>
                    <li>
                      <code className="font-mono">PROXYCHECK_API_KEY</code>
                    </li>
                    <li>
                      <code className="font-mono">TLS_WORKER_SECRET</code>
                    </li>
                    <li>
                      <code className="font-mono">JS_CHALLENGE_SECRET</code>
                    </li>
                  </ul>
                  <div className="mt-2 text-[11px] text-blue-700">
                    {t('botCloaking.thresholdsPanel.railwayHint')}
                  </div>
                </div>
              </div>

              {/* Footer actions */}
              <div className="border-t border-gray-200 bg-gray-50 px-5 py-3 flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs text-gray-500">
                  {dirtyKeys.length > 0 ? (
                    <span className="text-amber-700 font-medium">
                      {dirtyKeys.length === 1
                        ? t('botCloaking.thresholdsPanel.footerUnsavedSingle', {
                            count: dirtyKeys.length,
                          })
                        : t('botCloaking.thresholdsPanel.footerUnsavedPlural', {
                            count: dirtyKeys.length,
                          })}
                    </span>
                  ) : (
                    t('botCloaking.thresholdsPanel.footerSaved')
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleResetAll}
                    disabled={resetting || saving}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 text-xs font-medium text-gray-700 disabled:opacity-50"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    {t('botCloaking.thresholdsPanel.resetAll')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={
                      saving ||
                      resetting ||
                      dirtyKeys.length === 0 ||
                      Object.keys(validationErrors).length > 0
                    }
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-xs font-semibold text-white disabled:opacity-50"
                  >
                    <Save className="w-3.5 h-3.5" />
                    {saving
                      ? t('botCloaking.thresholdsPanel.saving')
                      : t('botCloaking.thresholdsPanel.saveChanges')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

interface FieldProps {
  entry: SchemaEntry;
  displayLabel: string;
  displayDescription: string;
  value: number | boolean;
  isDirty: boolean;
  error?: string;
  onNumberChange: (entry: SchemaEntry, value: string) => void;
  onBoolChange: (entry: SchemaEntry, value: boolean) => void;
  onResetField: (entry: SchemaEntry) => void;
}

const ThresholdField: React.FC<FieldProps> = ({
  entry,
  displayLabel,
  displayDescription,
  value,
  isDirty,
  error,
  onNumberChange,
  onBoolChange,
  onResetField,
}) => {
  const { t } = useTranslation('admin');
  const isBool = entry.cast === 'bool';
  return (
    <div
      className={`rounded-lg border p-3 transition ${
        isDirty ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-900">{displayLabel}</label>
            {isDirty && (
              <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-700">
                {t('botCloaking.thresholdsPanel.field.modified')}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{displayDescription}</p>
          <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-1.5">
            <span>
              {t('botCloaking.thresholdsPanel.field.default')}{' '}
              <code className="font-mono">{String(entry.defaultEffective)}</code>
            </span>
            {entry.envVar && (
              <span>
                · {t('botCloaking.thresholdsPanel.field.env')}{' '}
                <code className="font-mono">{entry.envVar}</code>
              </span>
            )}
          </div>
        </div>
        {isBool ? (
          <Toggle
            value={value as boolean}
            onChange={(v) => onBoolChange(entry, v)}
          />
        ) : (
          <div className="flex items-center gap-1 flex-shrink-0">
            <input
              type="number"
              value={value === undefined || value === null ? '' : String(value)}
              min={entry.min}
              max={entry.max}
              step={entry.step ?? (entry.cast === 'int' ? 1 : 0.01)}
              onChange={(e) => onNumberChange(entry, e.target.value)}
              className={`w-20 px-2 py-1 border rounded text-sm font-mono text-right focus:outline-none focus:ring-2 focus:ring-purple-400 ${
                error ? 'border-rose-400' : 'border-gray-300'
              }`}
            />
            {isDirty && (
              <button
                type="button"
                onClick={() => onResetField(entry)}
                title={t('botCloaking.thresholdsPanel.field.resetTitle')}
                className="p-1 text-gray-400 hover:text-gray-700"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
      {error && (
        <div className="text-xs text-rose-600 flex items-center gap-1 mt-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}
      {!isBool && (entry.min !== undefined || entry.max !== undefined) && (
        <div className="text-[11px] text-gray-400 mt-1">
          {t('botCloaking.thresholdsPanel.field.range')} {entry.min ?? '–∞'} …{' '}
          {entry.max ?? '+∞'}
        </div>
      )}
    </div>
  );
};

const Toggle: React.FC<{ value: boolean; onChange: (v: boolean) => void }> = ({
  value,
  onChange,
}) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition ${
      value ? 'bg-purple-600' : 'bg-gray-300'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
        value ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

export default CloakingThresholdsPanel;
